<template>
  <div class="preview-composition-page">
    <!-- Header avec actions -->
    <div class="card mb-4 border-0 shadow-sm">
      <div class="card-body p-4 d-flex justify-content-between align-items-center">
        <div>
          <router-link to="/liste-compositions" class="btn btn-outline-secondary me-2">
            <i class="fa fa-arrow-left me-2"></i>Retour
          </router-link>
          <button 
            @click="downloadPdf" 
            class="btn btn-primary me-2"
            :disabled="!composition || !composition.pdfUrl">
            <i class="fa fa-download me-2"></i>Télécharger PDF
          </button>
          <button 
            v-if="canEdit"
            @click="editComposition" 
            class="btn btn-warning me-2">
            <i class="fa fa-edit me-2"></i>Modifier
          </button>
          <button 
            v-if="canDelete"
            @click="deleteComposition" 
            class="btn btn-danger">
            <i class="fa fa-trash me-2"></i>Supprimer
          </button>
        </div>
        <div>
          <span class="badge bg-info fs-6" v-if="composition?.type">
            {{ composition.type === 'QCM' ? 'QCM' : 'Questions/Réponses' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Chargement -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
      <p class="mt-3 text-muted">Chargement de la composition...</p>
    </div>

    <!-- Erreur -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <i class="fa fa-exclamation-triangle me-2"></i>
      {{ error }}
    </div>

    <!-- Contenu de la composition -->
    <div v-else-if="composition" class="composition-preview">
      
      <!-- En-tête composition -->
      <div class="card mb-4 border-0 shadow-sm">
        <div class="card-header bg-primary text-white py-3">
          <h3 class="mb-0">
            <i class="fa fa-file-text-o me-2"></i>
            {{ composition.titre }}
          </h3>
        </div>
        <div class="card-body p-4">
          <div class="row">
            <div class="col-md-6">
              <div class="info-group mb-3">
                <label class="fw-bold text-muted small">MATIÈRE</label>
                <p class="mb-0 fs-5">
                  {{ composition.filiereNiveauMatiere?.matiere?.libelle || 'N/A' }}
                  <span class="badge bg-secondary ms-2">
                    {{ composition.filiereNiveauMatiere?.matiere?.code }}
                  </span>
                </p>
              </div>
              <div class="info-group mb-3">
                <label class="fw-bold text-muted small">FILIÈRE</label>
                <p class="mb-0 fs-6">{{ composition.filiereNiveauMatiere?.filiere?.libelle || 'N/A' }}</p>
              </div>
              <div class="info-group">
                <label class="fw-bold text-muted small">NIVEAU</label>
                <p class="mb-0 fs-6">{{ composition.filiereNiveauMatiere?.niveau?.libelle || 'N/A' }}</p>
              </div>
            </div>
            <div class="col-md-6">
              <div class="info-group mb-3">
                <label class="fw-bold text-muted small">PROFESSEUR</label>
                <p class="mb-0 fs-6">
                  {{ `${composition.professeur?.nom || ''} ${composition.professeur?.prenom || ''}`.trim() }}
                </p>
              </div>
              <div class="info-group mb-3">
                <label class="fw-bold text-muted small">DATE DE COMPOSITION</label>
                <p class="mb-0 fs-6">
                  <i class="fa fa-calendar me-2"></i>
                  {{ formatDate(composition.dateComposition) }}
                </p>
              </div>
              <div class="info-group" v-if="composition.dateFinSoumission">
                <label class="fw-bold text-muted small">DATE LIMITE SOUMISSION</label>
                <p class="mb-0 fs-6">
                  <i class="fa fa-clock-o me-2"></i>
                  {{ formatDate(composition.dateFinSoumission) }}
                  <span 
                    v-if="isExpired" 
                    class="badge bg-danger ms-2">
                    Expirée
                  </span>
                  <span 
                    v-else-if="daysRemaining <= 3" 
                    class="badge bg-warning ms-2">
                    J-{{ daysRemaining }}
                  </span>
                </p>
              </div>
              <div class="info-group" v-if="composition.filiereNiveauMatiere?.coefficient">
                <label class="fw-bold text-muted small">COEFFICIENT</label>
                <p class="mb-0">
                  <span class="badge bg-success fs-5">
                    {{ composition.filiereNiveauMatiere.coefficient }}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Questions -->
      <div class="card border-0 shadow-sm">
        <div class="card-header bg-secondary text-white py-3">
          <h4 class="mb-0">
            <i class="fa fa-question-circle me-2"></i>
            Questions ({{ composition.compoQuestions?.length || 0 }})
          </h4>
        </div>
        <div class="card-body p-4">
          
          <div v-if="!composition.compoQuestions || composition.compoQuestions.length === 0" 
               class="alert alert-warning">
            <i class="fa fa-info-circle me-2"></i>
            Aucune question n'a été ajoutée à cette composition.
          </div>

          <div v-else>
            <div 
              v-for="(cq, index) in composition.compoQuestions" 
              :key="cq.question.id"
              class="question-item mb-4 p-4 border rounded"
              :class="{'bg-light': index % 2 === 0}">
              
              <div class="d-flex align-items-start">
                <div class="question-number me-3">
                  <span class="badge bg-primary rounded-circle p-3 fs-5">
                    {{ index + 1 }}
                  </span>
                </div>
                <div class="flex-grow-1">
                  <p class="fs-6 mb-3 fw-medium">{{ cq.question.contenu }}</p>

                  <!-- Propositions QCM -->
                  <div v-if="cq.question.type === 'QCM' && cq.question.propositions?.length > 0" 
                       class="propositions ms-3">
                    <div 
                      v-for="(prop, pIndex) in cq.question.propositions" 
                      :key="prop.id"
                      class="form-check mb-2 p-2 rounded"
                      :class="{'bg-success bg-opacity-10 border border-success': prop.estLaBonneReponse}">
                      <input 
                        class="form-check-input" 
                        type="radio" 
                        disabled 
                        :checked="prop.estLaBonneReponse">
                      <label class="form-check-label ms-2">
                        <strong>{{ String.fromCharCode(65 + pIndex) }}.</strong> 
                        {{ prop.contenu }}
                        <span v-if="prop.estLaBonneReponse" class="badge bg-success ms-2">
                          ✓ Bonne réponse
                        </span>
                      </label>
                    </div>
                  </div>

                  <!-- Réponse attendue (Questions/Réponses) -->
                  <div v-else-if="cq.question.reponses?.length > 0" class="mt-3">
                    <div class="alert alert-info mb-0">
                      <strong>Réponse attendue :</strong>
                      <p class="mb-0 mt-2">{{ cq.question.reponses[0].contenu }}</p>
                    </div>
                  </div>

                  <!-- Pas de réponse -->
                  <div v-else class="mt-3">
                    <div class="alert alert-secondary mb-0">
                      <em>Aucune réponse modèle fournie</em>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/services/auth';
import ApiService from '@/services/ApiService';
import { error, success } from '@/utils/utils';
import Swal from 'sweetalert2';

export default defineComponent({
  name: 'PreviewComposition',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();

    const composition = ref<any>(null);
    const loading = ref(true);
    const errorMsg = ref('');

    const compositionId = computed(() => parseInt(route.params.id as string));

    const isProfesseur = computed(() => !!authStore.user?.professeur?.id);
    const isAdmin = computed(() => {
      const adminRoles = ['ADMIN', 'SUPER_ADMIN', 'Superadmin'];
      return adminRoles.includes(authStore.user?.role?.nom || '');
    });

    const canEdit = computed(() => {
      if (isAdmin.value) return true;
      if (isProfesseur.value && composition.value) {
        return composition.value.professeur?.id === authStore.user?.professeur?.id;
      }
      return false;
    });

    const canDelete = computed(() => canEdit.value);

    const isExpired = computed(() => {
      if (!composition.value?.dateFinSoumission) return false;
      const dateFin = new Date(composition.value.dateFinSoumission);
      dateFin.setHours(23, 59, 59, 999);
      return dateFin < new Date();
    });

    const daysRemaining = computed(() => {
      if (!composition.value?.dateFinSoumission) return 0;
      const dateFin = new Date(composition.value.dateFinSoumission);
      dateFin.setHours(23, 59, 59, 999);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return Math.max(0, Math.ceil((dateFin.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
    });

    const formatDate = (dateString: string) => {
      if (!dateString) return 'Non définie';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const fetchComposition = async () => {
      loading.value = true;
      try {
        const response = await ApiService.get(`/compositions/${compositionId.value}`);
        
        if (response.data.code === 200) {
          composition.value = response.data.data;
        } else {
          errorMsg.value = response.data.message || 'Erreur lors du chargement';
        }
      } catch (err: any) {
        console.error('Erreur chargement composition:', err);
        errorMsg.value = err.response?.data?.message || 'Impossible de charger la composition';
      } finally {
        loading.value = false;
      }
    };

    const downloadPdf = () => {
      if (!composition.value?.id) return;
      
      ApiService.get(`/compositions/${composition.value.id}/download-pdf`, { 
        responseType: 'blob' 
      })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${composition.value.titre.replace(/[^a-z0-9]/gi, '_')}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          success('PDF téléchargé avec succès');
        })
        .catch(() => {
          error('Erreur lors du téléchargement du PDF');
        });
    };

    const editComposition = () => {
      router.push({ name: 'EditCompositionPage', params: { id: compositionId.value } });
    };

    const deleteComposition = async () => {
      const result = await Swal.fire({
        title: 'Confirmer la suppression',
        text: `Voulez-vous vraiment supprimer la composition "${composition.value?.titre}" ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler'
      });

      if (result.isConfirmed) {
        try {
          await ApiService.delete(`/compositions/${compositionId.value}`);
          success('Composition supprimée avec succès');
          router.push('/liste-compositions');
        } catch (err: any) {
          error(err.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    };

    onMounted(() => {
      fetchComposition();
    });

    return {
      composition,
      loading,
      error: errorMsg,
      canEdit,
      canDelete,
      isExpired,
      daysRemaining,
      formatDate,
      downloadPdf,
      editComposition,
      deleteComposition
    };
  }
});
</script>

<style scoped>
.preview-composition-page {
  padding: 20px;
}

.info-group label {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.question-item {
  transition: all 0.3s ease;
}

.question-item:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.question-number {
  min-width: 50px;
}

.propositions .form-check {
  transition: background-color 0.2s ease;
}

.propositions .form-check:hover {
  background-color: rgba(0,0,0,0.02);
}
</style>